import React, {useState} from 'react';

import {assert} from 'Asserts';
import getMaxChipsToDisplay from 'notebook/tables/export/getMaxChipsToDisplay';
import {ArrayTypedValue, BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import tableMultiKeyDownHandler from 'notebook/tables/tableMultiKeyDownHandler';
import {formatTypedValue, parseTextAsFloat, parseTextAsInteger} from 'notebook/tables/typed-values/DataTypes';
import {
  FloatSchemaFieldTypedValue,
  IntegerSchemaFieldTypedValue,
  TextSchemaFieldTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {isUneditableField} from 'schemas/fields/SchemaFieldUtil';
import ArrayEditor from 'schemas/tags/ArrayEditor';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {CHIP_DISPLAY_LIMIT_TABLE_ROW} from 'ui-library/chips/ChipConstants';
import {BaseChipList} from 'ui-library/chips/ChipList';
import {ElementKeyboardEvent, Optional, assertNever} from 'util/Types';

type SubtypeValue = FloatSchemaFieldTypedValue | IntegerSchemaFieldTypedValue | TextSchemaFieldTypedValue;

const PRIMITIVE_SCHEMA_FIELD_TYPES: ReadonlyArray<SubtypeValue['type']> = [
  FieldType.FLOAT,
  FieldType.INTEGER,
  FieldType.TEXT,
];

/**
 * Returns whether `fieldType` is a multi-able primitive schema field type that you can use in an ArrayTypedValueInput.
 */
export function isPrimitiveMultiFieldType(fieldType: string): fieldType is SubtypeValue['type'] {
  return PRIMITIVE_SCHEMA_FIELD_TYPES.includes(fieldType as SubtypeValue['type']);
}

export interface ArrayTypedValueInputProps<S extends SubtypeValue> {
  subtype: S['type'];
  query?: string;
  typedValue?: TypedValue | null;
  onBlur?: () => void;
  onEscape: () => void;
  onSetCellData: (
    value: Optional<string>,
    cellData: {typedValue: TypedValue | null},
    enterKey?: boolean
  ) => void;
  onTabKey?: (e: ElementKeyboardEvent) => void;
  onUpdateQuery: (query: string) => void;
  onUpdateTypedValue: (typedValue: TypedValue) => void;
  autoFocus?: boolean;
  schemaField: SchemaField;
  // This will typically be rendered inside a table cell. If that's not the case, set this to true
  isOutsideTable?: boolean;
}

/**
 * Converts `value` into a TypedValue of type `type`. Returns null if it cannot be converted.
 */
function makeTypedValue(value: string, type: SubtypeValue['type']): SubtypeValue | null {
  if (type === FieldType.FLOAT) {
    const floatValue = parseTextAsFloat(value);
    return floatValue != null
      ? {
          type,
          value,
          rawValue: value,
          floatValue,
        }
      : null;
  } else if (type === FieldType.INTEGER) {
    const integerValue = parseTextAsInteger(value);
    return integerValue != null
      ? {
          type,
          value,
          integerValue,
        }
      : null;
  } else if (type === FieldType.TEXT) {
    return {
      type,
      value,
    };
  }
  assertNever(type);
  assert(false, `Disallowed type ${type}`);
}

/**
 * Splits `text` into subvalues by commas and new lines. Tries to convert each of those subvalues into `subtype` TypedValue. Also returns the subvalues that could not be interpreted as `subtype` TypedValue.
 *
 * @returns array of TypedValues along with the text that was not parsable as a typed value
 */
function textToTypedValues(
  text: string | undefined,
  subtype: SubtypeValue['type']
): [typedValues: Array<SubtypeValue>, nonParsableText: string] {
  if (!text) {
    return [[], ''];
  }

  const typedValues: Array<SubtypeValue> = [];
  const nonParsableValues: Array<string> = [];

  const splitValues = text
    .split(/\n|,/)
    .map((value) => value.trim())
    .filter((value) => value);
  for (const value of splitValues) {
    const typedValue = makeTypedValue(value, subtype);
    if (typedValue) {
      typedValues.push(typedValue);
    } else {
      nonParsableValues.push(value);
    }
  }
  return [typedValues, nonParsableValues.join(', ')];
}

/**
 * Component that allows the user to input an ArrayTypedValue into a table cell.
 * Each of the elements of ArrayTypedValue is a TypedValue of type `subtype`.
 */
export default function ArrayTypedValueInput<S extends SubtypeValue>(
  props: ArrayTypedValueInputProps<S>
): JSX.Element {
  const [typedValue, setTypedValue] = useState<ArrayTypedValue>(
    props.typedValue?.type === BaseTypedValueType.ARRAY
      ? props.typedValue
      : {
          type: BaseTypedValueType.ARRAY,
          rawValue: [],
        }
  );

  function onChange(finalizedValues: Array<TypedValue>): void {
    const newTypedValue: ArrayTypedValue = {
      type: BaseTypedValueType.ARRAY,
      rawValue: finalizedValues,
    };
    setTypedValue(newTypedValue);
    props.onUpdateTypedValue(newTypedValue);
  }

  function handleControlledKeyDown(event: ElementKeyboardEvent): void {
    tableMultiKeyDownHandler({
      event,
      value: typedValue ?? null,
      onSetValue: (typedValue: ArrayTypedValue, enterKey: boolean) => {
        if (!enterKey) {
          // Setting cellData when tabbing is handled by onTabKey
          return;
        }
        let newTypedValue = typedValue;
        const [values, _] = textToTypedValues(props.query, props.subtype);
        if (values.length) {
          newTypedValue = {...typedValue, rawValue: [...typedValue.rawValue, ...values]};
        }

        props.onSetCellData('', {typedValue: newTypedValue}, enterKey);
      },
      onEscape: props.onEscape,
      onTabKey: (tabEvent: ElementKeyboardEvent): void => {
        const [values, nonParsableText] = textToTypedValues(props.query, props.subtype);
        if (values.length) {
          const newTypedValue: ArrayTypedValue = {
            ...typedValue,
            rawValue: [...typedValue.rawValue, ...values],
          };
          setTypedValue(newTypedValue);
          props.onUpdateTypedValue(newTypedValue);
          // Leave the non-parsable text in the input so that the user can fix it.
          props.onUpdateQuery(nonParsableText);
        } else if (!nonParsableText) {
          // The user pressed tab without any non-whitespace text in the input, so user is trying to exit the cell editor.
          props.onSetCellData('', {typedValue}, false);
          props.onTabKey?.(tabEvent);
        }
      },
    });
  }

  if (isUneditableField(props.schemaField)) {
    return (
      <BaseChipList
        maxChips={getMaxChipsToDisplay(CHIP_DISPLAY_LIMIT_TABLE_ROW, false)}
        chips={typedValue.rawValue.map((value, i) => (
          <Chip key={i} size={ChipSize.TABLE_CELL} text={formatTypedValue(value)} />
        ))}
        chipSize={ChipSize.TABLE_CELL}
        isVertical={false}
      />
    );
  }

  return (
    <ArrayEditor
      autoFocus={props.autoFocus ?? true}
      inputValue={props.query || ''}
      onBlur={props.onBlur}
      onChange={onChange}
      onKeyDown={handleControlledKeyDown}
      onUpdateInputValue={props.onUpdateQuery}
      typedValue={typedValue}
      isOutsideTable={props.isOutsideTable}
    />
  );
}
